//
// Copyright 2024 DXOS.org
//

import * as Schema from 'effect/Schema';

import { invariant } from '@dxos/invariant';
import { type EntityId, PublicKey, SpaceId, type URI } from '@dxos/keys';
import { visitValues } from '@dxos/util';

import { type RawString } from './automerge.ts';
import type { ForeignKey } from './foreign-key.ts';
import { type EncodedReference, isEncodedReference } from './reference.ts';
import { type SpaceDocVersion } from './space-doc-version.ts';
import { createIdFromSpaceKey } from './space-id.ts';

/** Largest JSON (characters) and stored changes (bytes) a client backs an object with instead of its document. */
export const BACKED_OBJECT_LIMIT = 64 * 1024;

/**
 * Whether a client can back `structure` with its document's stored changes: the document's only object,
 * with no links, and not merged into another.
 */
export const isBackable = (doc: DatabaseDirectory, structure: EntityStructure): boolean =>
  Object.keys(doc.objects ?? {}).length === 1 &&
  Object.keys(doc.links ?? {}).length === 0 &&
  structure.system?.mergedInto === undefined;

/**
 * The JSON text a query row backs `structure` with, or `undefined` when it cannot be backed or the text
 * exceeds {@link BACKED_OBJECT_LIMIT}.
 */
export const backingJson = (doc: DatabaseDirectory, structure: EntityStructure, json: unknown): string | undefined => {
  if (!isBackable(doc, structure)) {
    return undefined;
  }
  const text = JSON.stringify(json);
  return text.length <= BACKED_OBJECT_LIMIT ? text : undefined;
};

export type SpaceState = {
  // Url of the root automerge document.
  rootUrl?: string;
};

/**
 * Array indexes get converted to strings.
 */
export type EntityProp = string;
export type EntityPropPath = EntityProp[];

/**
 * Escaped property path within an object.
 *
 * Escaping rules:
 *
 * - '.' -> '\.'
 * - '\' -> '\\'
 * - contact with .
 */
export const EscapedPropPath: Schema.Codec<string, string> & {
  escape: (path: EntityPropPath) => EscapedPropPath;
  unescape: (path: EscapedPropPath) => EntityPropPath;
} = class extends Schema.String.annotate({ title: 'EscapedPropPath' }) {
  static escape(path: EntityPropPath): EscapedPropPath {
    return path.map((p) => p.toString().replaceAll('\\', '\\\\').replaceAll('.', '\\.')).join('.');
  }

  static unescape(path: EscapedPropPath): EntityPropPath {
    const parts: string[] = [];
    let current = '';

    for (let i = 0; i < path.length; i++) {
      if (path[i] === '\\') {
        invariant(i + 1 < path.length && (path[i + 1] === '.' || path[i + 1] === '\\'), 'Malformed escaping.');
        current = current + path[i + 1];
        i++;
      } else if (path[i] === '.') {
        parts.push(current);
        current = '';
      } else {
        current += path[i];
      }
    }
    parts.push(current);

    return parts;
  }
};
export type EscapedPropPath = Schema.Schema.Type<typeof EscapedPropPath>;

/**
 * Link to all documents that hold objects in the space.
 */
export interface DatabaseDirectory {
  version?: SpaceDocVersion;

  access?: {
    /**
     * ID of the space that owns the document.
     */
    spaceId?: SpaceId;

    /**
     * @deprecated Use {@link spaceId}. Still written alongside `spaceId` so older clients
     * (and code paths that need the space public key, which cannot be recovered from the id)
     * keep working.
     *
     * Space key of the owning space in hex format without the 0x prefix.
     */
    spaceKey?: string;
  };
  /**
   * Objects inlined in the current document.
   */
  objects?: {
    [id: string]: EntityStructure;
  };
  /**
   * Object id points to an automerge doc url where the object is embedded.
   */
  links?: {
    [echoUri: string]: string | RawString;
  };

  /**
   * Per-object branch registry. Keyed by the subtree-root object id, then by branch name; each
   * branch records the automerge doc url holding each subtree member at that branch.
   *
   * This is the single synced source of truth for branches: it is both the branch list/membership
   * AND the set of branch documents the space must replicate (the host collects these urls in
   * {@link getAllBranchDocUrls}). The client document loader does NOT treat these urls as object
   * links, so branch docs never materialize as phantom objects. The implicit `'main'` branch is
   * never listed here (it is the object's main doc via {@link links}).
   *
   * Which branch a device is currently viewing is NOT stored here — that is device-local,
   * non-synced state.
   */
  branches?: SpaceBranchRegistry;

  /**
   * @deprecated
   * For backward compatibility.
   */
  experimental_spaceKey?: string;
}

/**
 * @see DatabaseDirectory.branches
 */
export type SpaceBranchRegistry = {
  [rootObjectId: string]: {
    [branchName: string]: BranchRecord;
  };
};

export type BranchRecord = {
  /** Subtree member object id -> automerge doc url holding that member on this branch. */
  members: { [objectId: string]: string | RawString };
  /**
   * The root object's main-doc heads at fork time. Provenance only — currently written but never
   * read; the merge relies on shared automerge ancestry, not this field.
   */
  baseHeads?: string[];
  /** Unix ms timestamp at branch creation. */
  createdAt?: number;
};

export const DatabaseDirectory = Object.freeze({
  /**
   * @returns ID of the space that owns the document.
   * Coalesces `access.spaceId` with the deprecated space key fields (`access.spaceKey`,
   * `experimental_spaceKey`), deriving the id from the key for documents that predate `spaceId`.
   */
  getSpaceId: async (doc: DatabaseDirectory): Promise<SpaceId | null> => {
    if (doc.access?.spaceId != null) {
      invariant(SpaceId.isValid(doc.access.spaceId), 'Invalid space ID');
      return doc.access.spaceId;
    }

    const spaceKeyHex = DatabaseDirectory.getSpaceKey(doc);
    if (spaceKeyHex == null) {
      return null;
    }

    return createIdFromSpaceKey(PublicKey.fromHex(spaceKeyHex));
  },

  /**
   * @deprecated Use {@link DatabaseDirectory.getSpaceId}. Only paths that require the space
   * public key (which cannot be derived from the space id) should read the key.
   *
   * @returns Space key in hex of the space that owns the document. In hex format. Without 0x prefix.
   */
  getSpaceKey: (doc: DatabaseDirectory): string | null => {
    // experimental_spaceKey is set on old documents, new ones are created with doc.access.spaceKey
    const rawSpaceKey = doc.access?.spaceKey ?? doc.experimental_spaceKey;
    if (rawSpaceKey == null) {
      return null;
    }

    const rawKey = String(rawSpaceKey);
    invariant(!rawKey.startsWith('0x'), 'Space key must not start with 0x');
    return rawKey;
  },

  getInlineObject: (doc: DatabaseDirectory, id: EntityId): EntityStructure | undefined => {
    return doc.objects?.[id];
  },

  getLink: (doc: DatabaseDirectory, id: EntityId): string | undefined => {
    return doc.links?.[id]?.toString();
  },

  /**
   * @returns The branch registry for a subtree-root object, or undefined if it has no branches.
   */
  getBranches: (doc: DatabaseDirectory, rootObjectId: EntityId): Record<string, BranchRecord> | undefined => {
    return doc.branches?.[rootObjectId];
  },

  /**
   * @returns All branch document urls referenced anywhere in the registry. Used by the host to
   * decide which documents to replicate (branch docs are NOT object links).
   */
  getAllBranchDocUrls: (doc: DatabaseDirectory): string[] => {
    const urls: string[] = [];
    for (const byName of Object.values(doc.branches ?? {})) {
      for (const record of Object.values(byName)) {
        for (const url of Object.values(record.members ?? {})) {
          urls.push(url.toString());
        }
      }
    }
    return urls;
  },

  make: ({
    spaceId,
    spaceKey,
    objects,
    links,
  }: {
    spaceId?: SpaceId;
    /**
     * @deprecated Provide {@link spaceId}. The key is still stamped for older clients.
     */
    spaceKey?: string;
    objects?: Record<string, EntityStructure>;
    links?: Record<string, RawString>;
  }): DatabaseDirectory => ({
    access: {
      ...(spaceId != null ? { spaceId } : {}),
      ...(spaceKey != null ? { spaceKey } : {}),
    },
    objects: objects ?? {},
    links: links ?? {},
  }),
});

/**
 * Representation of an ECHO object in an AM document.
 */
export type EntityStructure = {
  // TODO(dmaretskyi): Missing in some cases.
  system?: EntitySystem;

  meta: EntityMeta;
  /**
   * User-defined data.
   * Adheres to schema in `system.type`
   */
  data: Record<string, any>;
};

// Helper methods to interact with the {@link EntityStructure}.
export const EntityStructure = Object.freeze({
  /**
   * @throws On invalid object structure.
   */
  getTypeReference: (object: EntityStructure): EncodedReference | undefined => {
    return object.system?.type;
  },

  /**
   * @throws On invalid object structure.
   */
  getEntityKind: (object: EntityStructure): 'object' | 'relation' | 'type' => {
    const kind = object.system?.kind ?? 'object';
    invariant(kind === 'object' || kind === 'relation' || kind === 'type', 'Invalid kind');
    return kind;
  },

  isDeleted: (object: EntityStructure): boolean => {
    return object.system?.deleted ?? false;
  },

  getConvergenceKey: (object: EntityStructure): string | undefined => {
    return object.meta?.convergenceKey;
  },

  getMergedInto: (object: EntityStructure): EntityId | undefined => {
    return object.system?.mergedInto;
  },

  getMergedFrom: (object: EntityStructure): EntityId[] => {
    return object.system?.mergedFrom ?? [];
  },

  getRelationSource: (object: EntityStructure): EncodedReference | undefined => {
    return object.system?.source;
  },

  getRelationTarget: (object: EntityStructure): EncodedReference | undefined => {
    return object.system?.target;
  },

  getParent: (object: EntityStructure): EncodedReference | undefined => {
    return object.system?.parent;
  },

  /**
   * @returns All references in the data section of the object.
   */
  getAllOutgoingReferences: (object: EntityStructure): { path: EntityPropPath; reference: EncodedReference }[] => {
    const references: { path: EntityPropPath; reference: EncodedReference }[] = [];
    const visit = (path: EntityPropPath, value: unknown) => {
      if (isEncodedReference(value)) {
        references.push({ path, reference: value });
      } else {
        visitValues(value, (value, key) => visit([...path, String(key)], value));
      }
    };
    visitValues(object.data, (value, key) => visit([String(key)], value));
    return references;
  },

  getTags: (object: EntityStructure): (EncodedReference | string)[] => {
    return object.meta.tags ?? [];
  },

  makeObject: ({
    type,
    data,
    keys,
  }: {
    type: URI.URI;
    deleted?: boolean;
    keys?: ForeignKey[];
    data?: unknown;
  }): EntityStructure => {
    return {
      system: {
        kind: 'object',
        type: { '/': type },
      },
      meta: {
        keys: keys ?? [],
      },
      data: data ?? {},
    };
  },

  makeRelation: ({
    type,
    source,
    target,
    deleted,
    keys,
    data,
  }: {
    type: URI.URI;
    source: EncodedReference;
    target: EncodedReference;
    deleted?: boolean;
    keys?: ForeignKey[];
    data?: unknown;
  }): EntityStructure => {
    return {
      system: {
        kind: 'relation',
        type: { '/': type },
        source,
        target,
        deleted: deleted ?? false,
      },
      meta: {
        keys: keys ?? [],
      },
      data: data ?? {},
    };
  },

  makeType: ({ type, keys, data }: { type: URI.URI; keys?: ForeignKey[]; data?: unknown }): EntityStructure => {
    return {
      system: {
        kind: 'type',
        type: { '/': type },
      },
      meta: {
        keys: keys ?? [],
      },
      data: data ?? {},
    };
  },
});

/**
 * Echo object metadata.
 */
export type EntityMeta = {
  /**
   * Foreign keys.
   */
  keys: ForeignKey[];

  /**
   * Tags.
   * Encoded references to Tag objects within the space.
   *
   * NOTE: Optional for backwards compatibility; legacy data may store bare DXN strings, which are
   * upgraded to encoded references on read (see `object-core.ts`).
   */
  tags?: (EncodedReference | string)[];

  /**
   * Fully-qualified registry key for the object (FQN format, e.g. `com.example.type.foo`).
   * Identifies the canonical registry entry the object instance was created from.
   */
  key?: string;

  /**
   * Semantic version of the registry entry the object was created from.
   * Must be a valid semver string (e.g. `1.2.3`).
   */
  version?: string;

  /**
   * Caller-supplied domain identity, unique within a space: two entities carrying the same
   * convergence key are the same entity and are merged into one.
   *
   * Opaque to the storage engine — callers that need generations encode them in the string
   * (e.g. `com.example.seed@2`), which yields distinct entities because the strings differ.
   * Distinct from {@link key}/{@link version}, which record the registry entry an instance was
   * created from (provenance, not identity).
   */
  convergenceKey?: string;

  /**
   * Dictionary of annotations to this entity.
   *
   * NOTE: Optional for backwards compatibility. Values are arbitrary decoded automerge primitives;
   * typed as `any` so `EntityStructure` stays assignable to `DecodedAutomergePrimaryValue`.
   */
  annotations?: { readonly [key: string]: any };
};

/**
 * Automerge object system properties.
 * (Is automerge specific.)
 */
export type EntitySystem = {
  /**
   * Entity kind. `'type'` covers persisted ECHO type definitions (instances of
   * the `Type.Type` meta-schema); `'object'` / `'relation'` cover regular ECHO
   * instances.
   */
  kind?: 'object' | 'relation' | 'type';

  /**
   * Object reference ('protobuf' protocol) type — DXN of the schema this
   * entity instantiates.
   *
   * - For `kind === 'object'` / `'relation'` instances, this is the URI of the
   *   user-defined schema the entity was created from (e.g. `dxn:com.example.Person:1.0.0`).
   * - For `kind === 'type'` entities (persisted Type.Type meta-instances) this
   *   is always the URI of the `TypeSchema` meta-schema itself
   *   (`dxn:org.dxos.type.schema:0.1.0`). The kind that the meta-instance
   *   _describes_ (object/relation/type) lives in `data.jsonSchema.entityKind`.
   */
  type?: EncodedReference;

  /**
   * Deletion marker.
   */
  deleted?: boolean;

  /**
   * Object parent.
   * Objects with no parent are at the top level of the object hierarchy in the space.
   */
  parent?: EncodedReference;

  /**
   * Only for relations.
   */
  source?: EncodedReference;

  /**
   * Only for relations.
   */
  target?: EncodedReference;

  /**
   * Unix ms timestamp recorded at object creation time.
   * Set once when the ObjectStructure is first written; never modified after that.
   * Survives compaction / migrations (unlike automerge change timestamps).
   */
  createdAt?: number;

  /**
   * Id of the entity this one was merged into, set on the loser of a convergence-key merge.
   *
   * The loser is tombstoned but keeps replicating, so late peers can run the same merge and
   * follow the redirect. Every edge points at a smaller id, so chains are finite and acyclic
   * and terminate at the global minimum.
   */
  mergedInto?: EntityId;

  /**
   * The loser's automerge heads at the moment it was merged away.
   *
   * A later pass diffs the loser from these heads to fold in edits made by a peer that was
   * offline during the merge, instead of re-running the field-wise merge (which prefers the
   * smallest-id candidate and would discard those edits).
   */
  mergedAtHeads?: string[];

  /**
   * Ids of the entities merged into this one, set on the winner of a convergence-key merge.
   *
   * The reverse edge of {@link mergedInto}, stored rather than derived: finding it the other way
   * means scanning for entities whose `mergedInto` points here, which no index supports. Holding
   * it forward also lets a loaded winner reach its absorbed entities directly — what the
   * straggler fold needs when the merge is driven lazily.
   *
   * Transitively closed: when a chain collapses, the surviving entity absorbs the losers' own
   * lists too, so this always names every entity that folded into this one.
   */
  mergedFrom?: EntityId[];
};

/**
 * Id property name.
 */
export const PROPERTY_ID = 'id';

/**
 * Data namespace.
 * The key on {@link EntityStructure} that contains the user-defined data.
 */
export const DATA_NAMESPACE = 'data';
